/**
 * Extended Navigator interface to support the User-Agent Client Hints API
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData
 */
interface NavigatorUAData extends Navigator {
    userAgentData?: {
        mobile: boolean;
        platform?: string;
        brands?: Array<{ brand: string; version: string }>;
    };
}

/**
 * Detects if the current browser environment is running on a mobile device.
 *
 * This function uses the modern User-Agent Client Hints API (navigator.userAgentData.mobile)
 * when available, which is supported in:
 * - Chrome 90+ (April 2021)
 * - Edge 90+ (April 2021)
 * - Opera 76+ (May 2021)
 *
 * For browsers that don't support the User-Agent Client Hints API, it falls back to
 * traditional user agent string parsing to detect common mobile device patterns.
 *
 * @returns {boolean} `true` if the device is mobile, `false` otherwise
 *
 * @example
 * ```typescript
 * import { isMobile } from '@shared/utils/browserUtils';
 *
 * if (isMobile()) {
 *   console.log('User is on a mobile device');
 * }
 * ```
 */
export const isMobile = (): boolean => {
    // Check if we're in a browser environment
    if (typeof navigator === "undefined") {
        return false;
    }

    const nav = navigator as NavigatorUAData;
    let isMobileFromUAData = false;

    // Use the modern User-Agent Client Hints API if available
    if (nav.userAgentData && typeof nav.userAgentData.mobile === "boolean") {
        isMobileFromUAData = nav.userAgentData.mobile;
    }

    // Fallback to traditional user agent string parsing
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || "";

    // Check for common mobile device patterns
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;

    return isMobileFromUAData || mobileRegex.test(userAgent);
};
